import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../../utils/api';
import { getEventTitle } from '../../utils/modelLabels';
import {
  getViewedLogIds,
  markLogViewed,
  markLogsViewed,
  VIEWED_LOGS_CHANGED_EVENT,
} from '../../utils/viewedLogs';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { IconBell } from '../icons/Icons';
import './Notifications.css';

interface RecentEvent {
  id: number;
  timestamp: string | null;
  model: string;
  area_id: number;
  area_name: string;
  camera_id: number;
  camera_name: string;
  screen_url: string | null;
}

const EVENTS_LIMIT = 8;
const POLL_MS = 30_000;
const MAX_BADGE = 99;

/** «5 минут назад» / «вчера, 14:32» — компактная подпись для списка. */
function formatRelativeTime(timestamp: string | null): string {
  if (!timestamp) return '—';
  const date = new Date(timestamp.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} ч назад`;

  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (diffHours < 48) return `вчера, ${time}`;

  return `${date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}, ${time}`;
}

/**
 * Колокольчик уведомлений в верхней панели.
 *
 * Использует тот же эндпоинт последних детекций, что и дашборд,
 * и те же локальные отметки «просмотрено» — счётчик показывает,
 * сколько событий пользователь ещё не открывал.
 */
const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { requireSubscription } = useSubscription();

  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<RecentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [viewedIds, setViewedIds] = useState<Set<number>>(() => getViewedLogIds());
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const loadEvents = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) setLoading(true);
      const response = await dashboardAPI.getRecentEvents({ limit: EVENTS_LIMIT, offset: 0 });
      if (response.success) {
        setEvents(response.events);
        setError(false);
      }
    } catch {
      if (!options?.silent) setError(true);
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  // Первая загрузка + периодическое обновление в фоне
  useEffect(() => {
    void loadEvents();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadEvents({ silent: true });
      }
    }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadEvents({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadEvents]);

  // Отметки «просмотрено» могут меняться на дашборде — держим счётчик в курсе
  useEffect(() => {
    const sync = () => setViewedIds(getViewedLogIds());
    window.addEventListener(VIEWED_LOGS_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(VIEWED_LOGS_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Закрытие по клику вне и по Escape
  useEffect(() => {
    if (!open) return;

    const handleOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const unreadCount = useMemo(
    () => events.filter((event) => !viewedIds.has(event.id)).length,
    [events, viewedIds]
  );

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      // При открытии подтягиваем свежие события
      if (next) void loadEvents({ silent: events.length > 0 });
      return next;
    });
  };

  const handleOpenEvent = (event: RecentEvent) => {
    markLogViewed(event.id);
    setViewedIds(getViewedLogIds());
    setOpen(false);
    requireSubscription(() => navigate(`/area/${event.area_id}`));
  };

  const handleMarkAllRead = () => {
    markLogsViewed(events.map((event) => event.id));
    setViewedIds(getViewedLogIds());
  };

  return (
    <div className="notifications" ref={wrapRef}>
      <button
        type="button"
        className="topbar__icon-btn notifications__trigger"
        aria-label={
          unreadCount > 0 ? `Уведомления: ${unreadCount} новых` : 'Уведомления'
        }
        aria-expanded={open}
        aria-haspopup="menu"
        title="Уведомления"
        onClick={handleToggle}
      >
        <IconBell size={19} />
      </button>

      {unreadCount > 0 && (
        <span className="notifications__badge" aria-hidden="true">
          {unreadCount > MAX_BADGE ? `${MAX_BADGE}+` : unreadCount}
        </span>
      )}

      {open && (
        <div className="notifications__panel ui-menu" role="menu">
          <header className="notifications__head">
            <span className="notifications__title">Последние события</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notifications__mark-all"
                onClick={handleMarkAllRead}
              >
                Отметить прочитанными
              </button>
            )}
          </header>

          <div className="notifications__list ui-scroll">
            {loading ? (
              <div className="ui-loading">Загрузка…</div>
            ) : error ? (
              <div className="ui-empty">
                <span>Не удалось загрузить события</span>
                <button
                  type="button"
                  className="ui-btn ui-btn--outline ui-btn--sm"
                  onClick={() => void loadEvents()}
                >
                  Повторить
                </button>
              </div>
            ) : events.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">Событий пока нет</span>
                <span>Детекции появятся здесь сразу после срабатывания аналитик</span>
              </div>
            ) : (
              events.map((event) => {
                const isNew = !viewedIds.has(event.id);
                return (
                  <button
                    key={event.id}
                    type="button"
                    role="menuitem"
                    className={`notifications__item${
                      isNew ? ' notifications__item--new' : ''
                    }`}
                    onClick={() => handleOpenEvent(event)}
                  >
                    <span className="notifications__dot" aria-hidden="true" />
                    <span className="notifications__item-body">
                      <span className="notifications__item-title">
                        {getEventTitle(event.model)}
                      </span>
                      <span className="notifications__item-meta">
                        {event.area_name} • {event.camera_name}
                      </span>
                    </span>
                    <span className="notifications__item-time">
                      {formatRelativeTime(event.timestamp)}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <footer className="notifications__footer">
            <button
              type="button"
              className="notifications__all"
              onClick={() => {
                setOpen(false);
                navigate('/dashboard');
              }}
            >
              Все события
            </button>
          </footer>
        </div>
      )}
    </div>
  );
};

export default Notifications;
